import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, Phone, ExternalLink, ArrowLeft, Shield, Car, Home,
  Scale, Brain, DollarSign, UtensilsCrossed, Briefcase, Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { getAuthToken } from '@/utils/secureStorage';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const serviceIcons = {
  childcare: Shield,
  transportation: Car,
  mental_health: Brain,
  housing: Home,
  legal: Scale,
  coaching: Briefcase,
  food: UtensilsCrossed,
  financial: DollarSign
};

const serviceLabels = {
  childcare: 'Childcare',
  transportation: 'Transportation',
  mental_health: 'Mental Health',
  housing: 'Housing',
  legal: 'Legal Aid',
  coaching: 'Career Coaching',
  food: 'Food Assistance',
  financial: 'Financial Support'
};

export default function WraparoundHub() {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState('all');
  const [connecting, setConnecting] = useState(null);

  const fetchServices = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/workforce/wraparound`);
      if (res.ok) {
        const data = await res.json();
        setServices(data.services);
      }
    } catch (err) {
      console.error('Error fetching services:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleConnect = async (service) => {
    if (!user) {
      toast.error('Please log in to connect with services');
      return;
    }

    setConnecting(service.id);
    try {
      const token = getAuthToken();
      const res = await fetch(`${API}/api/workforce/participant/wraparound`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          service_type: service.type,
          notes: `Connected via Wraparound Hub`
        })
      });

      if (res.ok) {
        toast.success(`Connected with ${service.name}! Check your email for next steps.`);
      } else {
        const err = await res.json();
        if (err.detail?.includes('Not enrolled')) {
          toast.error('Please enroll in the program first');
        } else {
          throw new Error('Failed to connect');
        }
      }
    } catch (err) {
      toast.error('Failed to connect with service');
    } finally {
      setConnecting(null);
    }
  };

  const serviceTypes = ['all', ...new Set(services.map(s => s.type))];
  const filteredServices = activeType === 'all' 
    ? services 
    : services.filter(s => s.type === activeType);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-rose-50/30" data-testid="wraparound-hub">
      {/* Header */}
      <div className="bg-gradient-to-br from-rose-600 to-pink-700 text-white py-12">
        <div className="container mx-auto px-4">
          <Link to="/workforce" className="inline-flex items-center text-white/80 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Workforce Hub
          </Link>
          
          <div className="flex items-start gap-4">
            <div className="p-4 bg-white/20 rounded-xl">
              <Heart className="h-10 w-10" />
            </div>
            <div>
              <Badge className="mb-2 bg-white/20 text-white border-0">Support Services</Badge>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">Wraparound Support Hub</h1>
              <p className="text-rose-100 max-w-2xl">
                Life barriers shouldn't stop your career progress. We connect you to services 
                for childcare, transportation, housing, and more.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {serviceTypes.map(type => {
            const Icon = type === 'all' ? Search : (serviceIcons[type] || Heart);
            return (
              <button
                key={type}
                onClick={() => setActiveType(type)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeType === type 
                    ? 'bg-rose-600 text-white' 
                    : 'bg-white text-slate-600 hover:bg-slate-100 border'
                }`}
              >
                <Icon className="h-4 w-4" />
                {type === 'all' ? 'All Services' : serviceLabels[type] || type}
              </button>
            );
          })}
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {filteredServices.map((service, idx) => {
            const Icon = serviceIcons[service.type] || Heart;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-rose-100 text-rose-600">
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <Badge variant="outline" className="mb-2 text-xs">
                          {serviceLabels[service.type] || service.type}
                        </Badge>
                        <CardTitle className="text-lg">{service.name}</CardTitle>
                        <CardDescription className="text-sm">{service.provider}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 text-sm mb-4">{service.description}</p>
                    
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-slate-500">Eligibility:</span>
                        <p className="text-slate-700">{service.eligibility}</p>
                      </div>
                      
                      <div className="flex items-center gap-2 text-slate-700">
                        <Phone className="h-4 w-4 text-slate-400" />
                        {service.contact}
                      </div>
                    </div>

                    <div className="flex gap-2 mt-4 pt-4 border-t">
                      {service.url && (
                        <a
                          href={service.url.startsWith('/') ? service.url : service.url}
                          target={service.url.startsWith('/') ? '_self' : '_blank'}
                          rel="noopener noreferrer"
                          className="flex-1"
                        >
                          <Button variant="outline" className="w-full">
                            <ExternalLink className="h-4 w-4 mr-1" /> Visit Website
                          </Button>
                        </a>
                      )}
                      <Button 
                        className="flex-1 bg-rose-600 hover:bg-rose-700"
                        onClick={() => handleConnect(service)}
                        disabled={connecting === service.id}
                      >
                        {connecting === service.id ? 'Connecting...' : 'Connect Me'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Help Section */}
        <Card className="mt-12 bg-gradient-to-br from-rose-50 to-pink-50 border-rose-200">
          <CardContent className="py-8 text-center">
            <h3 className="text-xl font-bold text-slate-800 mb-2">Need Help Navigating?</h3>
            <p className="text-slate-600 mb-4">
              Our Workforce Guide can help identify which services are right for your situation.
            </p>
            <Link to="/workforce/assessment">
              <Button className="bg-rose-600 hover:bg-rose-700">
                Talk to the Guide <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
